// @flow

import * as React from 'react';
import classnames from '@ta/common.classnames';
import { Link } from '@ta/common.routing';
import type { UrlDescriptor } from '@ta/social.mentions-types';
import { ProtectedObfuscatedLink } from '@ta/social.obfuscation';
import { useMentionsInteractionContext } from '@ta/social.mentions-tracking';
import TripModalLink from '@ta/trips.trip-modal-links';
import styles from './UrlDescriptorLink.less';

type Props = {|
  urlDescriptor?: UrlDescriptor,
  className?: string,
  children: React.Node,
|};

/** A link that renders from a urlDescriptor.
 * If no urlDescriptor is provided, then children is wrapped in a span
 * If urlDescriptor has a route, then it is a TripModal navigation so its children is wrapped in a TripModalLink
 * If urlDescriptor has encoded set to true, then the children is wrapped in a ProtectedObfuscatedLink
 * If urlDescriptor has encoded set to false, then the children is wrapped in a simple <a> tag (from a Link)
 * */

const UrlDescriptorLink = ({ urlDescriptor, className, children }: Props) => {
  const InteractionTracking = useMentionsInteractionContext();
  if (!urlDescriptor) {
    return (
      <span className={className} onClick={InteractionTracking.feedClick || undefined}>
        {children}
      </span>
    );
  }

  // Case where its a Trip modal and so it requires special navigation
  if (urlDescriptor.navigator) {
    return (
      <TripModalLink
        className={className}
        tripModalDescriptor={urlDescriptor}
        onClick={InteractionTracking.feedClick || undefined}
      >
        {children}
      </TripModalLink>
    );
  }
  if (urlDescriptor.encoded) {
    return (
      <ProtectedObfuscatedLink
        className={classnames(styles.encoded_link, className)}
        encodedUrl={urlDescriptor.url}
        onClick={InteractionTracking.feedClick || undefined}
        target="_blank"
      >
        {children}
      </ProtectedObfuscatedLink>
    );
  }
  return (
    <Link
      className={className}
      href={urlDescriptor.url}
      onClick={InteractionTracking.feedClick || undefined}
    >
      {children}
    </Link>
  );
};

export default UrlDescriptorLink;
